import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api, fileUrl, API_URL } from '../lib/api';
import { displayPartCode } from '../lib/partCode';
import { Modal } from './Modal';
import { fmtCompact } from '../lib/dt';
import { BillsViewer } from './BillsManager';
import { Loading } from './Loading';
import { ApprovalActionPanel } from './ApprovalActionPanel';
import { ApprovalInsightCard } from './ApprovalInsightCard';
import { DocAttachments, type DocAttachment } from './DocAttachments';
import { useAuth } from '../lib/auth';
import { poTotals, poTotalsSummary } from '../lib/poTotals';

/**
 * Global stack of open document detail modals. Anywhere in the app, code can
 * call `useDocStack().open({ type, id })` to push a doc onto the stack. The
 * provider renders one Modal per stack entry, with progressively higher
 * z-index, so drilling from a PO into its GRN keeps the PO visible underneath.
 *
 * Supported doc types map to their detail endpoints:
 *   po          -> GET /api/pos/:id
 *   grn         -> GET /api/grns/:id
 *   issue       -> GET /api/issue-notes/:id
 *   indent      -> GET /api/indents/:id
 *   bill        -> GET /api/bills/:id
 *   vendor      -> GET /api/vendors/:id
 *   requisition -> GET /api/requisitions/:id
 *   partcode    -> GET /api/partcodes/:code
 *
 * Adding a new type means: extend DocType, add its detail renderer below,
 * and any DocLink with that type will Just Work.
 */

export type DocType =
  | 'po'
  | 'grn'
  | 'issue'
  | 'indent'
  | 'bill'
  | 'vendor'
  | 'requisition'
  | 'partcode'
  | 'onsite';

interface StackEntry {
  type: DocType;
  /** Mongo _id, sheet numeric id, or domain code (partcode). */
  id: string;
}

interface DocStackContext {
  open: (entry: StackEntry) => void;
  close: () => void;
  closeAll: () => void;
  stack: StackEntry[];
}

const Ctx = createContext<DocStackContext | null>(null);

export function useDocStack() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useDocStack used outside <DocStackProvider>');
  return v;
}

export function DocStackProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<StackEntry[]>([]);
  const open = useCallback((entry: StackEntry) => {
    if (!entry.id) return;
    setStack((s) => [...s, entry]);
  }, []);
  const close = useCallback(() => setStack((s) => s.slice(0, -1)), []);
  const closeAll = useCallback(() => setStack([]), []);

  // ESC pops the top of the stack instead of bubbling to outer handlers.
  useEffect(() => {
    if (!stack.length) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [stack.length, close]);

  return (
    <Ctx.Provider value={{ open, close, closeAll, stack }}>
      {children}
      {stack.map((entry, i) => (
        <DocModal
          key={`${entry.type}:${entry.id}:${i}`}
          entry={entry}
          // Each successive modal sits 10 z-index units above the parent.
          zIndex={50 + (i + 1) * 10}
          onClose={close}
        />
      ))}
    </Ctx.Provider>
  );
}

/**
 * Inline link styled to look like a button + underline. Click pushes the
 * referenced doc onto the stack. Renders an em-dash when id/label is empty
 * so callers can drop these into table cells without conditionals.
 */
export function DocLink({
  type, id, label, className = '',
}: {
  type: DocType;
  id: string | undefined | null;
  label?: string | undefined | null;
  className?: string;
}) {
  const { open } = useDocStack();
  const display = label || id || '';
  if (!id || !display) return <span className="text-slate-400">—</span>;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); open({ type, id }); }}
      className={`text-brand-700 hover:text-brand-900 underline decoration-dotted underline-offset-2 font-mono ${className}`}
      title={`Open ${type} ${display}`}
    >
      {display}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Per-type detail renderer dispatch.
// ---------------------------------------------------------------------------

function DocModal({ entry, zIndex, onClose }: { entry: StackEntry; zIndex: number; onClose: () => void }) {
  switch (entry.type) {
    case 'po':          return <PoDoc id={entry.id} zIndex={zIndex} onClose={onClose} />;
    case 'grn':         return <GrnDoc id={entry.id} zIndex={zIndex} onClose={onClose} />;
    case 'issue':       return <IssueDoc id={entry.id} zIndex={zIndex} onClose={onClose} />;
    case 'indent':      return <IndentDoc id={entry.id} zIndex={zIndex} onClose={onClose} />;
    case 'bill':        return <BillDoc id={entry.id} zIndex={zIndex} onClose={onClose} />;
    case 'vendor':      return <VendorDoc id={entry.id} zIndex={zIndex} onClose={onClose} />;
    case 'requisition': return <RequisitionDoc id={entry.id} zIndex={zIndex} onClose={onClose} />;
    case 'partcode':    return <PartCodeDoc id={entry.id} zIndex={zIndex} onClose={onClose} />;
    case 'onsite':      return <OnSiteDoc id={entry.id} zIndex={zIndex} onClose={onClose} />;
  }
}

// ---------- shared helpers ----------

const fmtDateTime = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

function FieldGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm pb-3 border-b border-slate-100">
      {children}
    </div>
  );
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div>{children ?? '—'}</div>
    </div>
  );
}

interface DocProps { id: string; zIndex: number; onClose: () => void }

// ---------- IssueNote ----------
function IssueDoc({ id, zIndex, onClose }: DocProps) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data, isLoading, error, refetch } = useQuery<any>({
    queryKey: ['doc-issue', id],
    queryFn: () => api.get(`/api/issue-notes/${encodeURIComponent(id)}`),
  });
  const ts = fmtCompact(data?.issueDate || data?.createdAt);
  const title = data?.issueNo ? `Issue Note — ${data.issueNo}${ts ? ` · ${ts}` : ''}` : 'Issue Note';
  const indentNo = data?.indent?.indentNo || data?.indentNo || '';
  const indentId = data?.indent?._id || data?.indent || '';
  const poNo = data?.po?.poNo || data?.poNo || '';
  const poId = data?.po?._id || data?.po || '';
  const grnNo = data?.grn?.grnNo || data?.grnNo || '';
  const grnId = data?.grn?._id || data?.grn || '';
  const reqNo = data?.requisition?.reqNo || '';
  const reqId = data?.requisition?._id || data?.requisition || '';
  const containerNo = data?.containerNo || data?.containerNumber || '';
  const locationName = typeof data?.location === 'string' ? data.location : data?.location?.name || '';

  // Manager / admin reject. Same shape as GrnDoc — but the stock effect is
  // opposite (issued qty returns to inventory) so the wording reflects that.
  const isManager = user?.role === 'manager' || user?.role === 'admin';
  const rejectableUntil = data?.rejectableUntil ? new Date(data.rejectableUntil) : null;
  const windowExpired = !!rejectableUntil && rejectableUntil.getTime() < Date.now();
  const canReject = isManager && !!data && !data.rejected && !windowExpired;
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const rejectMut = useMutation({
    mutationFn: (overrideReason?: string) =>
      api.post<{ stockReversal?: { reversed: number; failed: number; failures?: Array<{ itemName: string; reason?: string }> } }>(
        `/api/issue-notes/${encodeURIComponent(data?.issueNo || data?._id || id)}/reject`,
        { reason: (overrideReason ?? rejectReason).trim() || data?.rejection?.reason || 'reconciliation' },
      ),
    onSuccess: (r) => {
      const fr = r?.stockReversal;
      if (fr && fr.failed > 0) {
        toast.error(
          `Stock reversal partial: ${fr.reversed} reversed, ${fr.failed} failed (${(fr.failures || []).map((x) => x.itemName).join(', ')})`,
          { duration: 8000 },
        );
      } else if (fr && fr.reversed === 0 && data?.rejected) {
        toast.success(`Issue note ${data?.issueNo} — ledger already up to date`);
      } else {
        toast.success(`Issue note ${data?.issueNo} rejected — stock restored`);
      }
      setRejectOpen(false); setRejectReason('');
      refetch();
      qc.invalidateQueries({ queryKey: ['issue-notes'] });
      qc.invalidateQueries({ queryKey: ['partcode'] });
    },
    onError: (e: any) => toast.error(e.message || 'Failed to reject issue note'),
  });

  return (
    <Modal title={title} onClose={onClose} maxWidth="max-w-5xl" zIndex={zIndex}>
      {isLoading && <Loading size="sm" />}
      {error && <div className="p-4 text-sm text-red-600">Could not load issue note.</div>}
      {data && (
        <div className="space-y-4">
          {data.rejected && (
            <div className="border border-red-200 bg-red-50 rounded-md p-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded bg-red-600 text-white">Rejected</span>
                <span className="text-red-800 font-medium">This Issue Note has been rejected. Stock has been restored.</span>
              </div>
              <div className="mt-2 text-xs text-red-900 space-y-0.5">
                <div><span className="font-semibold">Reason: </span>{data.rejection?.reason || '—'}</div>
                <div>
                  <span className="font-semibold">By: </span>
                  {data.rejection?.rejectedBy?.name || data.rejection?.rejectedBy?.email || '—'}
                  {data.rejection?.rejectedAt && (
                    <> · <span className="font-mono">{fmtDateTime(data.rejection.rejectedAt)}</span></>
                  )}
                </div>
              </div>
              {isManager && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => rejectMut.mutate(data?.rejection?.reason || 'reconciliation')}
                    disabled={rejectMut.isPending}
                    className="text-[11px] px-2 py-1 rounded border border-red-300 bg-white text-red-700 hover:bg-red-100 disabled:opacity-50"
                    title="Re-run the stock reversal. Idempotent — only writes ledger entries that don't already exist."
                  >
                    {rejectMut.isPending ? 'Reconciling…' : 'Re-run stock reversal'}
                  </button>
                  <span className="ml-2 text-[10px] text-red-800/70">
                    Use if on-hand looks wrong — checks the ledger and writes any missing reversal entries.
                  </span>
                </div>
              )}
            </div>
          )}

          {isManager && !data.rejected && (
            <div className="flex justify-end gap-2">
              {!rejectOpen && (
                <>
                  {rejectableUntil && (
                    <span className={`self-center text-[10px] ${windowExpired ? 'text-slate-400' : 'text-slate-500'}`}>
                      {windowExpired
                        ? 'Rejection window closed'
                        : <>Reject window closes <span className="font-mono">{fmtDateTime(rejectableUntil.toISOString())}</span></>}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setRejectOpen(true)}
                    disabled={!canReject}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-red-300 bg-white text-red-700 hover:bg-red-50 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    title={windowExpired
                      ? 'Rejection window has closed — ask an admin if a correction is still needed.'
                      : 'Mark this Issue Note as rejected (duplicate / wrong qty / wrong item)'}
                  >
                    Reject Issue Note
                  </button>
                </>
              )}
            </div>
          )}

          {canReject && rejectOpen && (
            <div className="border border-red-200 bg-red-50 rounded-md p-3 space-y-2">
              <div className="text-xs font-semibold text-red-800">
                Reject Issue Note {data.issueNo}? The issued quantity will be added back to inventory automatically.
              </div>
              <label className="block text-xs text-red-900">
                <span className="block mb-1">Reason <span className="text-red-700">*</span> (e.g. duplicate issue, wrong technician, wrong quantity, wrong item)</span>
                <textarea
                  className="w-full text-sm px-2 py-1.5 border border-red-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-red-500"
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explain why this Issue Note is being rejected…"
                  autoFocus
                />
              </label>
              <div className="flex items-center gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => { setRejectOpen(false); setRejectReason(''); }}
                  className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50"
                  disabled={rejectMut.isPending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => rejectMut.mutate(undefined)}
                  disabled={!rejectReason.trim() || rejectMut.isPending}
                  className="text-xs px-3 py-1.5 rounded-md bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rejectMut.isPending ? 'Rejecting…' : 'Confirm reject'}
                </button>
              </div>
            </div>
          )}

          <FieldGrid>
            <Field label="Date">{fmtDateTime(data.issueDate || data.createdAt)}</Field>
            <Field label="Technician">{data.technicianName}</Field>
            <Field label="By">{data.createdBy?.name}</Field>
            <Field label="Total"><span className="font-mono">₹{Number(data.totalAmount || 0).toLocaleString('en-IN')}</span></Field>
            <Field label="Job Order">{data.jobOrderNo}</Field>
            <Field label="Container">{containerNo}</Field>
            <Field label="Requisition"><DocLink type="requisition" id={String(reqId || reqNo)} label={reqNo} /></Field>
            <Field label="Indent"><DocLink type="indent" id={String(indentId || indentNo)} label={indentNo} /></Field>
            <Field label="Location">{locationName}</Field>
            <Field label="PO"><DocLink type="po" id={String(poId || poNo)} label={poNo} /></Field>
            <Field label="GRN"><DocLink type="grn" id={String(grnId || grnNo)} label={grnNo} /></Field>
            <Field label="Dispatch mode">{String(data.dispatchMode || 'internal').replace('_', ' ')}</Field>
            {data.receivedByName && <Field label="Received by">{data.receivedByName}</Field>}
            {data.issueReason && (
              <Field label="Issue reason">{data.issueReason}{data.issueReasonNote ? ` — ${data.issueReasonNote}` : ''}</Field>
            )}
          </FieldGrid>

          {/* Courier / dispatch details — only when courier or vendor_direct */}
          {(data.dispatchMode === 'courier' || data.awbNo || data.courier || data.trackingStatus) && (
            <div>
              <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Dispatch &amp; tracking</div>
              <FieldGrid>
                <Field label="Courier">{data.courier || '—'}</Field>
                <Field label="AWB / Docket"><span className="font-mono">{data.awbNo || '—'}</span></Field>
                <Field label="Carrier">{data.trackingCarrier || '—'}</Field>
                <Field label="Tracking status">{data.trackingStatus || '—'}</Field>
                <Field label="Last checked">{data.trackingLastChecked ? fmtDateTime(data.trackingLastChecked) : '—'}</Field>
                <Field label="Delivered at">{data.deliveredAt ? fmtDateTime(data.deliveredAt) : '—'}</Field>
                <Field label="Delivered by">{data.deliveredByName || '—'}</Field>
                <Field label="Recipient">{data.recipientName || '—'}</Field>
                <Field label="Recipient email">{data.recipientEmail || '—'}</Field>
                <Field label="Recipient confirmed">{data.recipientConfirmedAt ? fmtDateTime(data.recipientConfirmedAt) : '—'}</Field>
              </FieldGrid>
              {data.recipientConfirmNote && (
                <div className="text-xs text-slate-600 mt-2">
                  <span className="text-[10px] uppercase text-slate-500 mr-1">Recipient note:</span>{data.recipientConfirmNote}
                </div>
              )}
            </div>
          )}

          <ItemsTable items={data.items || []} mode="issue" />
          <ApprovalInsightCard docType="issue" id={id} />
          <IssueOldPartsSection issueId={id} />

          {Array.isArray(data.trackingEvents) && data.trackingEvents.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Tracking events ({data.trackingEvents.length})</div>
              <ol className="border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100 text-xs">
                {data.trackingEvents.map((ev: any, i: number) => (
                  <li key={i} className="flex items-start gap-3 px-3 py-1.5">
                    <span className="font-mono text-slate-500 whitespace-nowrap">{ev.at ? fmtDateTime(ev.at) : '—'}</span>
                    <span className="text-slate-600 whitespace-nowrap">{ev.location || '—'}</span>
                    <span className="flex-1 text-slate-700">{ev.description || '—'}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {data.remarks && <Remarks text={data.remarks} />}
        </div>
      )}
    </Modal>
  );
}

// Read-only "where did the old part go" section for an Issue Note. Kept
// structurally separate from ItemsTable (shared with GrnDoc/PoDoc) so this
// has zero blast radius on those doc types. Self-fetching, renders nothing
// when the Issue Note has no old-part-disposition rows.
function IssueOldPartsSection({ issueId }: { issueId: string }) {
  const { data } = useQuery<any[]>({
    queryKey: ['issue-old-part-dispositions', issueId],
    queryFn: () => api.get(`/api/old-part-dispositions?issueNoteId=${encodeURIComponent(issueId)}`),
    enabled: !!issueId,
    staleTime: 60_000,
    retry: false,
  });
  if (!Array.isArray(data) || data.length === 0) return null;
  const label: Record<string, string> = {
    returned_to_stock: 'Returned to stock',
    sent_to_vendor: 'Sent to vendor',
    scrapped: 'Scrapped',
    kept_at_site: 'Kept at site',
  };
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Old parts replaced ({data.length})</div>
      <div className="overflow-x-auto">
        <table className="grid-table">
          <thead>
            <tr>
              <th>Old part</th>
              <th className="num">Qty</th>
              <th>Disposition</th>
              <th>Reason</th>
              <th>Vendor</th>
            </tr>
          </thead>
          <tbody>
            {data.map((r: any) => (
              <tr key={r._id}>
                <td>{r.oldPart?.description || '—'}</td>
                <td className="num">{r.qty}</td>
                <td>{label[r.disposition] || r.disposition}</td>
                <td>{[r.reason, r.reasonNote].filter(Boolean).join(' — ') || '—'}</td>
                <td>{r.vendorName || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- GRN ----------
function GrnDoc({ id, zIndex, onClose }: DocProps) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data, isLoading, error, refetch } = useQuery<any>({
    queryKey: ['doc-grn', id],
    queryFn: () => api.get(`/api/grns/${encodeURIComponent(id)}`),
  });
  const ts = fmtCompact(data?.grnDate || data?.createdAt);
  const title = data?.grnNo ? `GRN — ${data.grnNo}${ts ? ` · ${ts}` : ''}` : 'GRN';
  const poNo = data?.po?.poNo || data?.poNo || '';
  const poId = data?.po?._id || data?.po || '';
  const vendorName = data?.vendor?.name || '';
  const vendorId = data?.vendor?._id || data?.vendor || '';
  const indentNo = data?.indent?.indentNo || data?.indentNo || '';
  const indentId = data?.indent?._id || data?.indent || '';
  const locationName = typeof data?.location === 'string' ? data.location : data?.location?.name || '';
  // Print URL — same auto-print route the DocAttachments list already
  // points at. Carries the linked PO / Indent / job context as query
  // params so the printable header has them even when not stored on the
  // GRN doc itself.
  const printQs = new URLSearchParams();
  if (poNo) printQs.set('po', String(poNo));
  if (indentNo) printQs.set('indent', String(indentNo));
  if (data?.jobOrderNo) printQs.set('job', String(data.jobOrderNo));
  if (vendorName) printQs.set('vendor', String(vendorName));
  if (locationName) printQs.set('location', String(locationName));
  const printUrl = `/grns/${encodeURIComponent(String(data?._id || id))}/print${printQs.toString() ? `?${printQs.toString()}` : ''}`;

  // Reject GRN — manager/admin only and within the configured time window.
  // `rejectableUntil` comes from the backend (env-configurable, default 24h
  // after the GRN date); once expired the button is hidden and a small
  // hint explains why.
  const isManager = user?.role === 'manager' || user?.role === 'admin';
  // Admins bypass the rejection window entirely (escape hatch for late
  // corrections); managers stay bound to it.
  const isAdmin = user?.role === 'admin' || (user as any)?.master === true;
  const grnRejectableUntil = data?.rejectableUntil ? new Date(data.rejectableUntil) : null;
  const grnWindowExpired = !!grnRejectableUntil && grnRejectableUntil.getTime() < Date.now();
  const canReject = isManager && !!data && !data.rejected && (!grnWindowExpired || isAdmin);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const rejectMut = useMutation({
    mutationFn: (overrideReason?: string) =>
      api.post<{ stockReversal?: { reversed: number; failed: number; failures?: Array<{ itemName: string; reason?: string }> } }>(
        `/api/grns/${encodeURIComponent(data?.grnNo || data?._id || id)}/reject`,
        { reason: (overrideReason ?? rejectReason).trim() || data?.rejection?.reason || 'reconciliation' },
      ),
    onSuccess: (r) => {
      const fr = r?.stockReversal;
      if (fr && fr.failed > 0) {
        toast.error(
          `Stock reversal partial: ${fr.reversed} reversed, ${fr.failed} failed (${(fr.failures || []).map((x) => x.itemName).join(', ')})`,
          { duration: 8000 },
        );
      } else if (fr && fr.reversed === 0 && data?.rejected) {
        toast.success(`GRN ${data?.grnNo} — ledger already up to date`);
      } else {
        toast.success(`GRN ${data?.grnNo} rejected — stock reversed`);
      }
      setRejectOpen(false); setRejectReason('');
      refetch();
      qc.invalidateQueries({ queryKey: ['grns'] });
      qc.invalidateQueries({ queryKey: ['grn-bills-index'] });
      qc.invalidateQueries({ queryKey: ['partcode'] });
    },
    onError: (e: any) => toast.error(e.message || 'Failed to reject GRN'),
  });

  return (
    <Modal title={title} onClose={onClose} maxWidth="max-w-5xl" zIndex={zIndex}>
      {isLoading && <Loading size="sm" />}
      {error && <div className="p-4 text-sm text-red-600">Could not load GRN.</div>}
      {data && (
        <div className="space-y-4">
          {data.rejected && (
            <div className="border border-red-200 bg-red-50 rounded-md p-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded bg-red-600 text-white">Rejected</span>
                <span className="text-red-800 font-medium">This GRN has been rejected. Stock has been reversed.</span>
              </div>
              <div className="mt-2 text-xs text-red-900 space-y-0.5">
                <div><span className="font-semibold">Reason: </span>{data.rejection?.reason || '—'}</div>
                <div>
                  <span className="font-semibold">By: </span>
                  {data.rejection?.rejectedBy?.name || data.rejection?.rejectedBy?.email || '—'}
                  {data.rejection?.rejectedAt && (
                    <> · <span className="font-mono">{fmtDateTime(data.rejection.rejectedAt)}</span></>
                  )}
                </div>
              </div>
              {isManager && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => rejectMut.mutate(data?.rejection?.reason || 'reconciliation')}
                    disabled={rejectMut.isPending}
                    className="text-[11px] px-2 py-1 rounded border border-red-300 bg-white text-red-700 hover:bg-red-100 disabled:opacity-50"
                    title="Re-run the stock reversal. Idempotent — only writes ledger entries that don't already exist."
                  >
                    {rejectMut.isPending ? 'Reconciling…' : 'Re-run stock reversal'}
                  </button>
                  <span className="ml-2 text-[10px] text-red-800/70">
                    Use if on-hand looks wrong — checks the ledger and writes any missing reversal entries.
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end items-center gap-2">
            {isManager && !data.rejected && grnRejectableUntil && !rejectOpen && (
              <span className={`text-[10px] ${grnWindowExpired && !isAdmin ? 'text-slate-400' : 'text-slate-500'}`}>
                {grnWindowExpired
                  ? (isAdmin ? 'Window closed — admin override' : 'Rejection window closed')
                  : <>Reject window closes <span className="font-mono">{fmtDateTime(grnRejectableUntil.toISOString())}</span></>}
              </span>
            )}
            <a
              href={printUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 font-medium"
              title="Open printable GRN (auto-opens print dialog)"
            >
              🖨 Print
            </a>
            {isManager && !data.rejected && !rejectOpen && (
              <button
                type="button"
                onClick={() => setRejectOpen(true)}
                disabled={!canReject}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-red-300 bg-white text-red-700 hover:bg-red-50 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                title={grnWindowExpired
                  ? (isAdmin
                    ? 'Window closed, but admins can still reject — use only for genuine corrections (stock will be reversed).'
                    : 'Rejection window has closed — ask an admin if a correction is still needed.')
                  : 'Mark this GRN as rejected (duplicate / wrong qty / wrong item)'}
              >
                Reject GRN
              </button>
            )}
          </div>

          {canReject && rejectOpen && (
            <div className="border border-red-200 bg-red-50 rounded-md p-3 space-y-2">
              <div className="text-xs font-semibold text-red-800">
                Reject GRN {data.grnNo}? The stock entries from this GRN will be reversed.
              </div>
              <label className="block text-xs text-red-900">
                <span className="block mb-1">Reason <span className="text-red-700">*</span> (e.g. duplicate GRN, wrong quantity, wrong item)</span>
                <textarea
                  className="w-full text-sm px-2 py-1.5 border border-red-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-red-500"
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explain why this GRN is being rejected…"
                  autoFocus
                />
              </label>
              <div className="flex items-center gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => { setRejectOpen(false); setRejectReason(''); }}
                  className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50"
                  disabled={rejectMut.isPending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => rejectMut.mutate(undefined)}
                  disabled={!rejectReason.trim() || rejectMut.isPending}
                  className="text-xs px-3 py-1.5 rounded-md bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rejectMut.isPending ? 'Rejecting…' : 'Confirm reject'}
                </button>
              </div>
            </div>
          )}

          <FieldGrid>
            <Field label="Created">{fmtDateTime(data.grnDate || data.createdAt)}</Field>
            <Field label="Vendor"><DocLink type="vendor" id={String(vendorId)} label={vendorName} /></Field>
            <Field label="PO"><DocLink type="po" id={String(poId || poNo)} label={poNo} /></Field>
            <Field label="Vendor Bill">{data.vendorBillNo}</Field>
            <Field label="Job Order">{data.jobOrderNo}</Field>
            <Field label="Location">{locationName}</Field>
            <Field label="Indent"><DocLink type="indent" id={String(indentId || indentNo)} label={indentNo} /></Field>
            <Field label="Total"><span className="font-mono">₹{Number(data.totalAmount || 0).toLocaleString('en-IN')}</span></Field>
          </FieldGrid>
          {Array.isArray(data.bills) && data.bills.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Vendor bills attached ({data.bills.length})</div>
              <BillsViewer bills={data.bills} />
            </div>
          )}
          <ItemsTable items={data.items || []} mode="grn" showBillRef={Array.isArray(data.bills) && data.bills.length > 0} />
          <ApprovalInsightCard docType="grn" id={id} />
          {data.remarks && <Remarks text={data.remarks} />}
        </div>
      )}
    </Modal>
  );
}

// ---------- PO ----------
function PoDoc({ id, zIndex, onClose }: DocProps) {
  const { data, isLoading, error } = useQuery<any>({
    queryKey: ['doc-po', id],
    queryFn: () => api.get(`/api/pos/${encodeURIComponent(id)}`),
  });
  const ts = fmtCompact(data?.createdAt);
  const title = data?.poNo ? `Purchase Order — ${data.poNo}${ts ? ` · ${ts}` : ''}` : 'Purchase Order';
  const indentNo = data?.indent?.indentNo || data?.indentNo || '';
  const indentId = data?.indent?._id || data?.indent || '';
  const vendorName = data?.vendor?.name || '';
  const vendorId = data?.vendor?._id || data?.vendor || '';
  return (
    <Modal title={title} onClose={onClose} maxWidth="max-w-5xl" zIndex={zIndex}>
      {isLoading && <Loading size="sm" />}
      {error && <div className="p-4 text-sm text-red-600">Could not load PO.</div>}
      {data && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <PoStatusPill status={data.status} />
            <a
              href={`/pos/${encodeURIComponent(String(data._id || id))}/print`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 font-medium"
              title="Open printable PO (auto-opens print dialog)"
            >
              🖨 Print
            </a>
          </div>
          <ApprovalActionPanel kind="po" docId={id} status={data.status} />
          {/* Overview — clean label/value matrix instead of a wrapping grid. */}
          <InfoTable rows={[
            ['Vendor', <DocLink type="vendor" id={String(vendorId)} label={vendorName || '—'} />],
            ['Type', data.reqType ? <ReqTypeBadge reqType={data.reqType} orderNo={data.orderReceivedNo} /> : null],
            ['VR No.', data.vrNo || null],
            ['Indent', indentNo ? <DocLink type="indent" id={String(indentId || indentNo)} label={indentNo} /> : null],
            ['Job Order', data.jobOrderNo || null],
            ['Container', data.containerNo || null],
            ['Generated', `${fmtDateTime(data.createdAt)}${data.generatedBy?.name ? ` · ${data.generatedBy.name}` : ''}`],
            ['GRN', data.grnNo ? <DocLink type="grn" id={String(data.grnNo)} label={data.grnNo} /> : null],
            ['Issue Note', data.issueNoteNumber ? <DocLink type="issue" id={String(data.issueNoteNumber)} label={data.issueNoteNumber} /> : null],
            ['Invoice', data.invoiceNumber || null],
            // NET payable, with the breakdown spelled out — `totalAmount` alone
            // is goods-only and disagreed with the PO detail modal.
            ['Total', (() => {
              const t = poTotals(data);
              return (
                <span>
                  <span className="font-mono font-semibold">₹{t.net.toLocaleString('en-IN')}</span>
                  {t.hasBreakup && (
                    <span className="block text-[10px] font-normal text-slate-500">{poTotalsSummary(data)}</span>
                  )}
                </span>
              );
            })()],
          ]} />
          {/* Requisition source remarks BEFORE items for approver context. */}
          <RequisitionSourceBlock data={data} />
          <ItemsTable items={data.items || []} mode="po" />
          <PoAccountsTrail data={data} />
        </div>
      )}
    </Modal>
  );
}

const PO_STATUS_META: Record<string, string> = {
  pending_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  sent: 'bg-sky-100 text-sky-800',
  partial: 'bg-indigo-100 text-indigo-800',
  closed: 'bg-slate-200 text-slate-700',
  rejected: 'bg-red-100 text-red-700',
};
// Origin of the requisition/PO — Job Order / Order Number / IMS Stock Replenish.
const REQ_TYPE_META: Record<string, { label: string; cls: string }> = {
  job_order: { label: 'Job Order', cls: 'bg-blue-100 text-blue-700' },
  order_number: { label: 'Order No', cls: 'bg-indigo-100 text-indigo-700' },
  ims_reorder: { label: 'IMS Stock Replenish', cls: 'bg-emerald-100 text-emerald-700' },
  on_site: { label: 'On-Site', cls: 'bg-amber-100 text-amber-700' },
};
export function ReqTypeBadge({ reqType, orderNo }: { reqType?: string; orderNo?: string }) {
  const m = REQ_TYPE_META[String(reqType || '')];
  if (!m) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold ${m.cls}`}>
      {m.label}{orderNo ? <span className="font-mono font-normal opacity-80">· {orderNo}</span> : null}
    </span>
  );
}

function PoStatusPill({ status }: { status?: string }) {
  const s = String(status || '').toLowerCase();
  const cls = PO_STATUS_META[s] || 'bg-slate-100 text-slate-600';
  return <span className={`px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wide ${cls}`}>{s.replace(/_/g, ' ') || '—'}</span>;
}

// Clean label/value matrix. Rows whose value is null/undefined/'' are dropped so
// the panel never shows empty "—" clutter.
function InfoTable({ rows }: { rows: Array<[string, ReactNode]> }) {
  const visible = rows.filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (!visible.length) return null;
  return (
    <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
      <tbody className="divide-y divide-slate-100">
        {visible.map(([label, val], i) => (
          <tr key={i} className="even:bg-slate-50/40">
            <td className="px-3 py-1.5 text-slate-500 w-40 align-top whitespace-nowrap">{label}</td>
            <td className="px-3 py-1.5 text-slate-800 break-words">{val}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * "Requisition source + ALL remarks" — aggregates every remark/note typed
 * on this PO and its source requisition (general remark, per-line notes,
 * approval-chain notes from BOTH the requisition's chain AND the PO's
 * chain) plus the requisition's attachments. Same data the PO detail
 * page on /pos shows; keeps the modal surface consistent. NOT rendered
 * into the printable PO PDF.
 */
function RequisitionSourceBlock({ data }: { data: any }) {
  type RemarkEntry = {
    source: 'req-general' | 'req-line' | 'req-approval' | 'po-line' | 'po-approval' | 'po-adhoc';
    label: string;
    sublabel?: string;
    text: string;
    at?: string;
    attachments?: Array<{ id: string; filename: string }>;
  };
  const req = data?.requisition;
  const reqIsRef = req && typeof req === 'object';
  const remarks: RemarkEntry[] = [];
  if (reqIsRef && String(req.reason || '').trim()) {
    remarks.push({ source: 'req-general', label: 'Requisition · general remark', sublabel: req.reqNo, text: String(req.reason).trim() });
  }
  if (reqIsRef && Array.isArray(req.items)) {
    req.items.forEach((it: any, i: number) => {
      const t = String(it?.note || '').trim();
      const atts = Array.isArray(it?.attachments) ? it.attachments : [];
      if (!t && atts.length === 0) return;
      const label = it?.partCode?.code || it?.partCodeText || it?.itemName || `Line ${i + 1}`;
      remarks.push({ source: 'req-line', label: `Requisition line · ${label}`, text: t || `(${atts.length} attachment${atts.length === 1 ? '' : 's'})`, attachments: atts });
    });
  }
  if (reqIsRef && Array.isArray(req.approvals)) {
    req.approvals.forEach((s: any) => {
      const t = String(s?.note || '').trim();
      if (!t) return;
      const who = s?.byEmail || s?.by?.email || s?.by?.name || 'approver';
      remarks.push({ source: 'req-approval', label: `Requisition approval · ${s.ruleName || `stage ${s.stageIndex + 1}`} (${s.status})`, sublabel: who, text: t, at: s.at || undefined });
    });
  }
  const poChain: any[] = Array.isArray(data?.approvalChain) && data.approvalChain.length
    ? data.approvalChain
    : (Array.isArray(data?.approvals) ? data.approvals : []);
  poChain.forEach((s: any) => {
    const t = String(s?.note || '').trim();
    if (!t) return;
    const who = s?.byName || s?.byEmail || s?.by?.name || s?.by?.email || 'approver';
    remarks.push({ source: 'po-approval', label: `PO approval · ${s.ruleName || `stage ${(s.stageIndex ?? 0) + 1}`} (${s.status})`, sublabel: who, text: t, at: s.at || undefined });
  });
  if (Array.isArray(data?.items)) {
    data.items.forEach((it: any, i: number) => {
      const t = String(it?.note || '').trim();
      const atts = Array.isArray(it?.attachments) ? it.attachments : [];
      if (!t && atts.length === 0) return;
      const label = it?.partCode?.code || it?.partCodeText || it?.itemName || `Line ${i + 1}`;
      remarks.push({ source: 'po-line', label: `PO line · ${label}`, text: t || `(${atts.length} attachment${atts.length === 1 ? '' : 's'})`, attachments: atts });
    });
  }
  // 6. PO ad-hoc remarks added from the detail page (text + uploaded files).
  if (Array.isArray(data?.remarks)) {
    data.remarks.forEach((r: any, i: number) => {
      const t = String(r?.text || '').trim();
      const atts = Array.isArray(r?.attachments) ? r.attachments : [];
      if (!t && atts.length === 0) return;
      const who = r?.byName || r?.byEmail || 'operator';
      remarks.push({
        source: 'po-adhoc',
        label: `PO remark #${i + 1}`,
        sublabel: who,
        text: t || `(${atts.length} attachment${atts.length === 1 ? '' : 's'})`,
        at: r?.at || undefined,
        attachments: atts,
      });
    });
  }
  const reqAttachments: Array<{ id: string; filename: string }> = reqIsRef && Array.isArray(req.attachments) ? req.attachments : [];
  if (!remarks.length && reqAttachments.length === 0) return null;
  const fmtDate = (s?: string) => {
    if (!s) return '';
    const d = new Date(s);
    return isNaN(d.getTime()) ? '' : d.toLocaleString('en-IN');
  };
  // Requisition-side entries get a sky accent, PO-side a violet one, so the
  // two conversations are visually distinct at a glance.
  const isReq = (src: RemarkEntry['source']) => src.startsWith('req');
  const accent = (src: RemarkEntry['source']) => (isReq(src) ? 'border-sky-400' : 'border-violet-400');
  const chip = (src: RemarkEntry['source']) => (isReq(src)
    ? 'bg-sky-100 text-sky-700'
    : 'bg-violet-100 text-violet-700');
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">Remarks &amp; source</span>
        {reqIsRef && req.reqNo && <span className="text-[10px] font-mono text-slate-500">{req.reqNo}</span>}
        <span className="ml-auto text-[10px] text-slate-400">
          {remarks.length} remark{remarks.length === 1 ? '' : 's'}
          {reqAttachments.length ? ` · ${reqAttachments.length} file${reqAttachments.length === 1 ? '' : 's'}` : ''}
        </span>
      </div>
      {remarks.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {remarks.map((r, i) => (
            <li key={i} className={`border-l-2 ${accent(r.source)} pl-3 pr-3 py-2`}>
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide ${chip(r.source)}`}>
                  {r.label}
                </span>
                <div className="text-[10px] text-slate-400 font-mono whitespace-nowrap flex items-center gap-2">
                  {r.sublabel && <span className="normal-case">{r.sublabel}</span>}
                  {r.at && <span>{fmtDate(r.at)}</span>}
                </div>
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap leading-snug">{r.text}</div>
              {Array.isArray(r.attachments) && r.attachments.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {r.attachments.map((att) => (
                    <a
                      key={att.id} href={fileUrl(att.id)} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 bg-white hover:bg-slate-50 border border-slate-200 text-slate-600 text-[11px] font-mono rounded px-2 py-0.5"
                      title={`Open/Download ${att.filename}`}
                    >
                      📎 <span className="truncate max-w-[160px]">{att.filename}</span>
                    </a>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {reqAttachments.length > 0 && (
        <div className="px-3 py-2 bg-slate-50/50 border-t border-slate-100">
          <div className="text-[10px] uppercase text-slate-500 mb-1.5">Requisition attachments ({reqAttachments.length})</div>
          <div className="flex flex-wrap gap-2">
            {reqAttachments.map((att) => (
              <a
                key={att.id} href={fileUrl(att.id)} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 bg-white hover:bg-sky-50 border border-sky-200 text-sky-800 text-xs font-mono rounded px-2.5 py-1.5 transition-colors"
                title={`Open/Download ${att.filename}`}
              >
                📎 <span className="truncate max-w-[200px]">{att.filename}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Requisition ----------
function RequisitionDoc({ id, zIndex, onClose }: DocProps) {
  const { data, isLoading, error } = useQuery<any>({
    queryKey: ['doc-requisition', id],
    queryFn: () => api.get(`/api/requisitions/${encodeURIComponent(id)}`),
  });
  const ts = fmtCompact(data?.createdAt);
  const title = data?.reqNo ? `Requisition — ${data.reqNo}${ts ? ` · ${ts}` : ''}` : 'Requisition';
  const indentNo = data?.indent?.indentNo || '';
  const indentId = data?.indent?._id || '';
  const vendorName = data?.selectedVendor?.name || '';
  const vendorId = data?.selectedVendor?._id || '';
  const locationName = typeof data?.location === 'string' ? data.location : data?.location?.name || '';
  return (
    <Modal title={title} onClose={onClose} maxWidth="max-w-5xl" zIndex={zIndex}>
      {isLoading && <Loading size="sm" />}
      {error && <div className="p-4 text-sm text-red-600">Could not load requisition.</div>}
      {data && (
        <div className="space-y-4">
          <ApprovalActionPanel
            kind="requisition"
            docId={id}
            status={data.status}
            approvals={Array.isArray(data.approvals) ? data.approvals : undefined}
            approvalEmails={Array.isArray(data.approvalEmails) ? data.approvalEmails : undefined}
          />
          <FieldGrid>
            <Field label="Status">{data.status}</Field>
            <Field label="Job Order">{data.jobOrderNo || '—'}</Field>
            <Field label="Container">{data.containerNo || '—'}</Field>
            <Field label="Requested by">{data.requestedBy?.name || '—'}</Field>
            <Field label="Location">{locationName || '—'}</Field>
            <Field label="Dispatch method">{String(data.dispatchMethod || 'undecided').replace(/_/g, ' ')}</Field>
            <Field label="Estimated total"><span className="font-mono">₹{Number(data.totalAmount || data.approvalEstimatedAmount || 0).toLocaleString('en-IN')}</span></Field>
            {/* Legacy sheet-sourced field only — client name must never
                surface for an order-number requisition (that data comes
                from orderContext.customerName, a completely different
                source, and is deliberately never wired here). */}
            {data.reqType !== 'order_number' && <Field label="Client">{data.clientName || '—'}</Field>}
            <Field label="Created">{fmtDateTime(data.createdAt)}</Field>
            <Field label="Updated">{fmtDateTime(data.updatedAt)}</Field>
            <Field label="Indent">{indentNo ? <DocLink type="indent" id={String(indentId || indentNo)} label={indentNo} /> : '—'}</Field>
            <Field label="Vendor">{vendorName ? <DocLink type="vendor" id={String(vendorId)} label={vendorName} /> : '—'}</Field>
          </FieldGrid>

          {data.reason && (
            <div className="text-xs text-slate-600">
              <div className="text-[10px] uppercase text-slate-500 mb-1">Reason</div>
              <div className="whitespace-pre-wrap">{data.reason}</div>
            </div>
          )}

          <RequisitionItemsTable items={data.items || []} />

          {data.approval && (data.approval.by || data.approval.at || data.approval.note) && (
            <div>
              <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Approval</div>
              <FieldGrid>
                <Field label="Status">{data.status === 'approved' ? '✓ Approved' : data.status === 'rejected' ? '✕ Rejected' : data.status}</Field>
                <Field label="By">{data.approval.by?.name || data.approval.by || '—'}</Field>
                <Field label="At">{fmtDateTime(data.approval.at)}</Field>
                <Field label="Note">{data.approval.note || '—'}</Field>
              </FieldGrid>
            </div>
          )}

          {data.vendorFinalization && (data.vendorFinalization.by || data.vendorFinalization.at) && (
            <div>
              <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Vendor finalisation</div>
              <FieldGrid>
                <Field label="By">{data.vendorFinalization.by?.name || data.vendorFinalization.by || '—'}</Field>
                <Field label="At">{fmtDateTime(data.vendorFinalization.at)}</Field>
              </FieldGrid>
            </div>
          )}

          {data.aiExtraction?.rawInput && (
            <div className="text-xs text-slate-600">
              <div className="text-[10px] uppercase text-slate-500 mb-1">AI extraction input</div>
              <div className="font-mono text-[11px] bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap">{data.aiExtraction.rawInput}</div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function RequisitionItemsTable({ items }: { items: any[] }) {
  if (!items.length) return <div className="text-sm text-slate-500 italic">No item details.</div>;
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Items ({items.length})</div>
      <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
        <thead className="text-xs uppercase text-slate-500 bg-slate-50">
          <tr>
            <th className="text-left px-2 py-1.5">#</th>
            <th className="text-left px-2 py-1.5">Item</th>
            <th className="text-left px-2 py-1.5">Part Code</th>
            <th className="text-left px-2 py-1.5">MFG Code</th>
            <th className="text-left px-2 py-1.5">Condition</th>
            <th className="text-right px-2 py-1.5">Qty</th>
            <th className="text-left px-2 py-1.5">Unit</th>
            <th className="text-right px-2 py-1.5">Est. Rate</th>
            <th className="text-right px-2 py-1.5">Est. Amount</th>
            <th className="text-left px-2 py-1.5">Stock</th>
            <th className="text-left px-2 py-1.5">Note</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((it, i) => {
            const code = displayPartCode(it);
            const name = it.itemName || (typeof it.partCode === 'object' ? it.partCode?.itemName : '') || '';
            const qty = Number(it.quantity ?? 0);
            const rate = Number(it.estimatedRate ?? 0);
            const amount = qty * rate;
            const onHand = it.stock?.onHand ?? null;
            return (
              <tr key={i}>
                <td className="px-2 py-1.5 text-slate-500">{i + 1}</td>
                <td className="px-2 py-1.5">{name}</td>
                <td className="px-2 py-1.5 font-mono text-xs">{code ? <DocLink type="partcode" id={code} label={code} /> : '—'}</td>
                <td className="px-2 py-1.5 font-mono text-xs">{it.mfgPartCode || '—'}</td>
                <td className="px-2 py-1.5 text-xs uppercase">{it.condition || '—'}</td>
                <td className="px-2 py-1.5 text-right font-mono">{qty}</td>
                <td className="px-2 py-1.5 text-xs">{it.qtyUnit || 'NOS'}</td>
                <td className="px-2 py-1.5 text-right font-mono">₹{rate.toLocaleString('en-IN')}</td>
                <td className="px-2 py-1.5 text-right font-mono">₹{amount.toLocaleString('en-IN')}</td>
                <td className="px-2 py-1.5 text-xs font-mono">{onHand == null ? '—' : onHand}</td>
                <td className="px-2 py-1.5 text-xs text-slate-600">{it.note || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Indent ----------
function IndentDoc({ id, zIndex, onClose }: DocProps) {
  const { data, isLoading, error } = useQuery<any>({
    queryKey: ['doc-indent', id],
    queryFn: () => api.get(`/api/indents/${encodeURIComponent(id)}`),
  });
  const ts = fmtCompact(data?.createdAt || data?.raisedAt);
  const title = data?.indentNo ? `Indent — ${data.indentNo}${ts ? ` · ${ts}` : ''}` : 'Indent';
  const vendorName = data?.vendor?.name || '';
  const vendorId = data?.vendor?._id || '';
  const reqNo = data?.requisition?.reqNo || '';
  const reqId = data?.requisition?._id || data?.requisition || '';
  return (
    <Modal title={title} onClose={onClose} maxWidth="max-w-5xl" zIndex={zIndex}>
      {isLoading && <Loading size="sm" />}
      {error && <div className="p-4 text-sm text-red-600">Could not load indent.</div>}
      {data && (
        <div className="space-y-4">
          <FieldGrid>
            <Field label="Status">{data.displayStatusLabel || data.status}</Field>
            <Field label="Raised by">{data.raisedBy?.name || '—'}</Field>
            <Field label="Raised at">{fmtDateTime(data.raisedAt || data.createdAt)}</Field>
            <Field label="Total"><span className="font-mono">₹{Number(data.totalAmount || 0).toLocaleString('en-IN')}</span></Field>
            <Field label="Vendor">{vendorName ? <DocLink type="vendor" id={String(vendorId)} label={vendorName} /> : '—'}</Field>
            <Field label="Requisition">{reqNo ? <DocLink type="requisition" id={String(reqId)} label={reqNo} /> : (reqId ? <DocLink type="requisition" id={String(reqId)} label="open" /> : '—')}</Field>
            <Field label="Items">{(data.items || []).length}</Field>
            <Field label="Updated">{fmtDateTime(data.updatedAt)}</Field>
          </FieldGrid>

          <IndentItemsTable items={data.items || []} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ApprovalBlock title="Technical approval" stage={data.technical} />
            <ApprovalBlock title="Commercial approval" stage={data.commercial} />
          </div>

          {Array.isArray(data.quoteAttempts) && data.quoteAttempts.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Rejected quote attempts ({data.quoteAttempts.length})</div>
              <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                <thead className="text-xs uppercase text-slate-500 bg-slate-50">
                  <tr>
                    <th className="text-left px-2 py-1.5">Stage</th>
                    <th className="text-left px-2 py-1.5">Vendor</th>
                    <th className="text-right px-2 py-1.5">Rate</th>
                    <th className="text-right px-2 py-1.5">Total</th>
                    <th className="text-left px-2 py-1.5">Rejected by</th>
                    <th className="text-left px-2 py-1.5">When</th>
                    <th className="text-left px-2 py-1.5">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.quoteAttempts.map((q: any, i: number) => (
                    <tr key={i}>
                      <td className="px-2 py-1.5 text-xs uppercase">{q.stage}</td>
                      <td className="px-2 py-1.5 text-xs">{q.vendorName || '—'}</td>
                      <td className="px-2 py-1.5 text-right font-mono">₹{Number(q.rate || 0).toLocaleString('en-IN')}</td>
                      <td className="px-2 py-1.5 text-right font-mono">₹{Number(q.totalAmount || 0).toLocaleString('en-IN')}</td>
                      <td className="px-2 py-1.5 text-xs">{q.rejectedByName || '—'}</td>
                      <td className="px-2 py-1.5 text-xs">{fmtDateTime(q.rejectedAt)}</td>
                      <td className="px-2 py-1.5 text-xs text-slate-600">{q.rejectionReason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function IndentItemsTable({ items }: { items: any[] }) {
  if (!items.length) return <div className="text-sm text-slate-500 italic">No item details.</div>;
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Items ({items.length})</div>
      <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
        <thead className="text-xs uppercase text-slate-500 bg-slate-50">
          <tr>
            <th className="text-left px-2 py-1.5">#</th>
            <th className="text-left px-2 py-1.5">Item</th>
            <th className="text-left px-2 py-1.5">Part Code</th>
            <th className="text-left px-2 py-1.5">MFG Code</th>
            <th className="text-right px-2 py-1.5">Qty</th>
            <th className="text-left px-2 py-1.5">Unit</th>
            <th className="text-right px-2 py-1.5">Rate</th>
            <th className="text-right px-2 py-1.5">Amount</th>
            <th className="text-left px-2 py-1.5">Note</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((it: any, i: number) => {
            const code = displayPartCode(it);
            const name = it.itemName || (typeof it.partCode === 'object' ? it.partCode?.itemName : '') || '';
            const qty = Number(it.quantity ?? 0);
            const rate = Number(it.rate ?? 0);
            const amount = Number(it.amount ?? qty * rate);
            return (
              <tr key={i}>
                <td className="px-2 py-1.5 text-slate-500">{i + 1}</td>
                <td className="px-2 py-1.5">{name}</td>
                <td className="px-2 py-1.5 font-mono text-xs">{code ? <DocLink type="partcode" id={code} label={code} /> : '—'}</td>
                <td className="px-2 py-1.5 font-mono text-xs">{it.mfgPartCode || '—'}</td>
                <td className="px-2 py-1.5 text-right font-mono">{qty}</td>
                <td className="px-2 py-1.5 text-xs">{it.qtyUnit || 'NOS'}</td>
                <td className="px-2 py-1.5 text-right font-mono">₹{rate.toLocaleString('en-IN')}</td>
                <td className="px-2 py-1.5 text-right font-mono">₹{amount.toLocaleString('en-IN')}</td>
                <td className="px-2 py-1.5 text-xs text-slate-600">{it.note || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ApprovalBlock({ title, stage }: { title: string; stage: any }) {
  const status = stage?.status || 'pending';
  const tone = status === 'approved' ? 'bg-emerald-100 text-emerald-800'
    : status === 'rejected' ? 'bg-red-100 text-red-800'
    : 'bg-amber-100 text-amber-800';
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide">{title}</div>
        <span className={`pill ${tone}`}>{status}</span>
      </div>
      <div className="text-xs space-y-1">
        <div><span className="text-slate-500">By:</span> {stage?.by?.name || stage?.by || '—'}</div>
        <div><span className="text-slate-500">At:</span> {fmtDateTime(stage?.at)}</div>
        <div><span className="text-slate-500">Note:</span> <span className="text-slate-700 whitespace-pre-wrap">{stage?.note || '—'}</span></div>
      </div>
    </div>
  );
}

// ---------- Bill / Vendor / PartCode ----------
// Lighter-weight stubs — they fetch the doc and show whatever fields the
// backend returns. Better to surface raw data than nothing while these
// modals get their bespoke layouts later.
// Vendor bill — a formatted summary + the ACTUAL bill file(s) rendered inline
// (image inline, PDF/other via an embedded frame), instead of a raw JSON dump.
function BillDoc({ id, zIndex, onClose }: DocProps) {
  const { data, isLoading, error } = useQuery<any>({
    queryKey: ['doc-bill', id],
    queryFn: () => api.get(`/api/bills/${encodeURIComponent(id)}`),
  });
  const title = data?.billNo ? `Bill — ${data.billNo}` : 'Bill';
  const atts: string[] = Array.isArray(data?.attachments)
    ? data.attachments.map((a: any) => String(a?._id || a?.id || a)).filter(Boolean)
    : [];
  return (
    <Modal title={title} onClose={onClose} maxWidth="max-w-4xl" zIndex={zIndex}>
      {isLoading && <Loading size="sm" />}
      {error && <div className="p-4 text-sm text-red-600">Could not load bill.</div>}
      {data && (
        <div className="space-y-4">
          <FieldGrid>
            <Field label="Bill No">{data.billNo}</Field>
            <Field label="Bill Date">{fmtDateTime(data.billDate)}</Field>
            <Field label="Amount"><span className="font-mono">₹{Number(data.billAmount || 0).toLocaleString('en-IN')}</span></Field>
            <Field label="Status">{data.status}</Field>
            <Field label="Vendor"><DocLink type="vendor" id={String(data.vendor?._id || data.vendor || '')} label={data.vendor?.name || '—'} /></Field>
            <Field label="PO"><DocLink type="po" id={String(data.po?._id || data.po || '')} label={data.po?.poNo || '—'} /></Field>
            <Field label="GRN"><DocLink type="grn" id={String(data.grn?.grnNo || data.grn?._id || '')} label={data.grn?.grnNo || '—'} /></Field>
            <Field label="Submitted">{fmtDateTime(data.submittedAt || data.createdAt)}{data.submittedBy?.name ? ` · ${data.submittedBy.name}` : ''}</Field>
            {data.paidAt && <Field label="Paid">{fmtDateTime(data.paidAt)}</Field>}
          </FieldGrid>
          {data.decision?.note && (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
              <b>Decision:</b> {data.decision.note}{data.decision.by?.name ? <span className="text-slate-500"> — {data.decision.by.name}</span> : null}
            </div>
          )}
          <div>
            <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">
              Bill document{atts.length > 1 ? `s (${atts.length})` : ''}
            </div>
            {atts.length === 0 ? (
              <div className="text-sm text-slate-500 italic">No bill file attached.</div>
            ) : (
              <div className="space-y-3">
                {atts.map((aid) => <BillAttachment key={aid} id={aid} />)}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// Renders one bill attachment: tries an <img> (invoice scans), falling back to
// an embedded frame for PDFs / non-images. Always offers an "open in new tab".
function BillAttachment({ id }: { id: string }) {
  const [notImage, setNotImage] = useState(false);
  const url = fileUrl(id);
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
      <div className="flex items-center justify-end px-2 py-1 border-b border-slate-200 bg-white">
        <a href={url} target="_blank" rel="noreferrer" className="text-[11px] text-brand-700 hover:underline">Open in new tab ↗</a>
      </div>
      {!notImage ? (
        <img src={url} alt="Bill document" className="block max-h-[70dvh] w-auto mx-auto" onError={() => setNotImage(true)} />
      ) : (
        <iframe src={url} title="Bill document" className="w-full h-[70dvh] border-0 bg-white" />
      )}
    </div>
  );
}
function VendorDoc({ id, zIndex, onClose }: DocProps) { return <GenericDoc title="Vendor" endpoint={`/api/vendors/${encodeURIComponent(id)}`} zIndex={zIndex} onClose={onClose} />; }
function PartCodeDoc({ id, zIndex, onClose }: DocProps) { return <GenericDoc title="PartCode" endpoint={`/api/partcodes/${encodeURIComponent(id)}`} zIndex={zIndex} onClose={onClose} />; }

// ---------- On-site purchase ----------
function OnSiteDoc({ id, zIndex, onClose }: DocProps) {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<any>({
    queryKey: ['doc-onsite', id],
    queryFn: () => api.get(`/api/on-site-purchases/${encodeURIComponent(id)}`),
  });
  const ts = fmtCompact(data?.purchasedAt || data?.createdAt);
  const title = data?.onSiteNo ? `On-Site Purchase — ${data.onSiteNo}${ts ? ` · ${ts}` : ''}` : 'On-Site Purchase';
  const approve = useMutation({
    mutationFn: (approved: boolean) => api.post(`/api/on-site-purchases/${id}/approve`, { approved }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['doc-onsite', id] }); qc.invalidateQueries({ queryKey: ['my-pending'] }); },
  });
  const reimburse = useMutation({
    mutationFn: () => api.post(`/api/on-site-purchases/${id}/reimburse`, {
      amount: data?.totalAmount || 0,
      mode: 'reimbursement',
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['doc-onsite', id] }); qc.invalidateQueries({ queryKey: ['my-pending'] }); },
  });

  const indentNo = data?.indent?.indentNo || '';
  const indentId = data?.indent?._id || '';
  const statusTone = data?.status === 'approved' ? 'bg-emerald-100 text-emerald-800'
    : data?.status === 'rejected' ? 'bg-red-100 text-red-800'
    : data?.status === 'reimbursed' ? 'bg-sky-100 text-sky-800'
    : 'bg-amber-100 text-amber-800';
  return (
    <Modal title={title} onClose={onClose} maxWidth="max-w-4xl" zIndex={zIndex}>
      {isLoading && <Loading size="sm" />}
      {error && <div className="p-4 text-sm text-red-600">Could not load on-site purchase.</div>}
      {data && (
        <div className="space-y-4">
          <FieldGrid>
            <Field label="Status">
              <span className={`pill ${statusTone}`}>{String(data.status || '').replace(/_/g, ' ')}</span>
            </Field>
            <Field label="Job Order">{data.jobOrderNo}</Field>
            <Field label="Container">{data.containerNo || '—'}</Field>
            <Field label="Site">{data.siteLocation || '—'}</Field>
            <Field label="Indent">
              {indentNo ? <DocLink type="indent" id={String(indentId || indentNo)} label={indentNo} /> : '—'}
            </Field>
            <Field label="Bills attached">
              {Array.isArray(data.bills) && data.bills.length > 0
                ? `${data.bills.length} bill${data.bills.length === 1 ? '' : 's'}`
                : (data.vendorBillNo ? `1 (${data.vendorBillNo})` : '—')}
            </Field>
            <Field label="Total"><span className="font-mono">₹{Number(data.totalAmount || 0).toLocaleString('en-IN')}</span></Field>
            <Field label="Purchased By">{data.purchasedBy?.name || data.purchasedByName || '—'}</Field>
            <Field label="Purchased At">{fmtCompact(data.purchasedAt || data.createdAt)}</Field>
            <Field label="Approval Rule">{data.approval?.ruleName || 'auto-approved'}</Field>
            <Field label="Approver">
              {data.approval?.by?.name
                ? `${data.approval.by.name}${data.approval.at ? ` · ${fmtCompact(data.approval.at)}` : ''}`
                : (data.approval?.requiredEmails?.length
                  ? `Pending: ${data.approval.requiredEmails.join(', ')}`
                  : '—')}
            </Field>
          </FieldGrid>

          {Array.isArray(data.bills) && data.bills.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Vendor bills attached ({data.bills.length})</div>
              <BillsViewer bills={data.bills} />
            </div>
          )}

          {/* Payment proofs — any type of file (image / PDF / doc / bank
              slip etc.) with a "paid by" tag. Add new ones inline; existing
              ones render as image thumbnails (with click-to-zoom lightbox)
              or file chips. */}
          <PaymentProofsPanel onSiteId={id} proofs={data.paymentProofs || []} />

          <div>
            <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Items purchased</div>
            <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
              <thead className="text-xs uppercase text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left px-2 py-1.5">#</th>
                  <th className="text-left px-2 py-1.5">Spare</th>
                  <th className="text-left px-2 py-1.5">Part Code</th>
                  <th className="text-left px-2 py-1.5">Condition</th>
                  <th className="text-right px-2 py-1.5">Qty</th>
                  <th className="text-right px-2 py-1.5">Rate</th>
                  <th className="text-right px-2 py-1.5">Amount</th>
                  <th className="text-left px-2 py-1.5">Bill</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data.items || []).map((it: any, i: number) => {
                  const code = displayPartCode(it);
                  const billRef = typeof it.billRef === 'number' ? it.billRef : -1;
                  const bill = billRef >= 0 && data.bills?.[billRef] ? data.bills[billRef] : null;
                  return (
                    <tr key={i}>
                      <td className="px-2 py-1.5 text-slate-500">{i + 1}</td>
                      <td className="px-2 py-1.5">{it.itemName}</td>
                      <td className="px-2 py-1.5 font-mono text-xs">{code ? <DocLink type="partcode" id={code} label={code} /> : '—'}</td>
                      <td className="px-2 py-1.5 text-xs uppercase">{it.condition || '—'}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{it.quantity}</td>
                      <td className="px-2 py-1.5 text-right font-mono">₹{Number(it.rate || 0).toLocaleString('en-IN')}</td>
                      <td className="px-2 py-1.5 text-right font-mono">₹{Number(it.amount || 0).toLocaleString('en-IN')}</td>
                      <td className="px-2 py-1.5 text-xs">
                        {bill ? (
                          <span className="font-mono text-slate-600" title={`${bill.vendorName || ''} · ${bill.vendorBillNo || ''}`}>
                            #{billRef + 1}
                          </span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Generated procurement documents — downloadable PDFs (print
              pages open in a new tab; Save as PDF). Auto-created with the
              on-site purchase. */}
          {(() => {
            const g = data.generatedDocs || {};
            const indents: any[] = g.indents || [];
            const pos: any[] = g.pos || [];
            const grns: any[] = g.grns || [];
            const issueNotes: any[] = g.issueNotes || [];
            if (!indents.length && !pos.length && !grns.length && !issueNotes.length) return null;
            const [client, location] = String(data.siteLocation || '').split(' · ');
            const ctx = new URLSearchParams();
            if (client) ctx.set('client', client);
            if (location) ctx.set('location', location);
            if (data.containerNo) ctx.set('container', data.containerNo);
            if (data.jobOrderNo) ctx.set('job', data.jobOrderNo);
            const qs = ctx.toString() ? `?${ctx.toString()}` : '';
            const attachments: DocAttachment[] = [
              ...indents.map((d) => ({ key: `ind-${d._id}`, kind: 'Indent', label: `Indent ${d.indentNo || ''}`.trim(), url: `/indents/${d._id}/print${qs}` })),
              ...pos.map((d) => ({ key: `po-${d._id}`, kind: 'PO', label: `PO ${d.poNo || ''}`.trim(), url: `/pos/${d._id}/print` })),
              ...grns.map((d) => ({ key: `grn-${d._id}`, kind: 'GRN', label: `GRN ${d.grnNo || ''}`.trim(), url: `/grns/${d._id}/print${qs}` })),
              ...issueNotes.map((d) => ({ key: `iss-${d._id}`, kind: 'Issue Note', label: `Issue Note ${d.issueNo || ''}`.trim(), url: `/issue-notes/${d._id}/print${qs}` })),
            ];
            return <DocAttachments docs={attachments} />;
          })()}

          {data.remarks && <Remarks text={data.remarks} />}

          {/* Approve / Reject — only when pending */}
          {data.status === 'pending_approval' && (
            <div className="flex gap-2 border-t border-slate-100 pt-3">
              <button
                className="px-3 py-2 text-sm rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-medium disabled:opacity-50"
                disabled={approve.isPending}
                onClick={() => approve.mutate(true)}
              >
                Approve purchase
              </button>
              <button
                className="px-3 py-2 text-sm rounded-md border border-red-300 hover:bg-red-50 text-red-700 font-medium disabled:opacity-50"
                disabled={approve.isPending}
                onClick={() => approve.mutate(false)}
              >
                Reject
              </button>
            </div>
          )}
          {data.status === 'approved' && (
            <div className="flex gap-2 border-t border-slate-100 pt-3">
              <button
                className="px-3 py-2 text-sm rounded-md bg-sky-600 hover:bg-sky-700 text-white font-medium disabled:opacity-50"
                disabled={reimburse.isPending}
                onClick={() => reimburse.mutate()}
              >
                Mark reimbursed (₹{Number(data.totalAmount || 0).toLocaleString('en-IN')})
              </button>
            </div>
          )}
          {data.status === 'reimbursed' && (
            <div className="text-xs text-slate-600 border-t border-slate-100 pt-2">
              Reimbursed ₹{Number(data.reimbursement?.amount || 0).toLocaleString('en-IN')}
              {data.reimbursement?.mode ? ` via ${data.reimbursement.mode}` : ''}
              {data.reimbursement?.paidAt ? ` · ${fmtCompact(data.reimbursement.paidAt)}` : ''}
              {data.reimbursement?.paidBy?.name ? ` · by ${data.reimbursement.paidBy.name}` : ''}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/**
 * Payment proofs section for the on-site purchase detail. Shows existing
 * proofs (images render as 96px thumbnails with click-to-zoom lightbox,
 * other types as file chips) and an inline upload form with a "paid by"
 * combo-box (dropdown + free-text — type a new name to add it to the
 * master list on first save).
 */
function PaymentProofsPanel({ onSiteId, proofs }: { onSiteId: string; proofs: any[] }) {
  const qc = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);
  const [paidBy, setPaidBy] = useState('');
  const [note, setNote] = useState('');
  const [customPaidBy, setCustomPaidBy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [lightbox, setLightbox] = useState<{ url: string; filename: string } | null>(null);

  const { data: payersData } = useQuery<{ payers: string[] }>({
    queryKey: ['onsite-payers'],
    queryFn: () => api.get('/api/on-site-purchases/payers'),
    staleTime: 60_000,
  });
  const payers = payersData?.payers || ['Sitaram', 'Accounts'];

  const upload = useMutation({
    mutationFn: async () => {
      if (!files.length) throw new Error('Pick at least one file');
      const fd = new FormData();
      for (const f of files) fd.append('files', f);
      if (paidBy.trim()) fd.append('paidBy', paidBy.trim());
      if (note.trim()) fd.append('note', note.trim());

      // Attempt up to 3 times with backoff. A single dropped request or
      // tab-suspend during the upload used to silently lose proofs — the
      // form reset on apparent-success even though the request never
      // completed. Now: every attempt is logged, retries cover transient
      // network blips, and the response payload is VERIFIED to contain
      // at least as many proofs as we sent before we trust it.
      const token = localStorage.getItem('ims_token');
      const url = `${API_URL}/api/on-site-purchases/${onSiteId}/payment-proofs`;
      let lastError: any = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const res = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: fd,
          });
          if (!res.ok) {
            const body = await res.json().catch(() => null);
            const msg = body?.error || `Failed (${res.status})`;
            // 4xx is a real rejection — don't retry, the next attempt will
            // get the same answer. Only retry on 5xx / network errors.
            if (res.status >= 400 && res.status < 500) throw new Error(msg);
            lastError = new Error(msg);
          } else {
            const data = await res.json();
            // Verify the server actually saved the proofs. If the response
            // has no paymentProofs array OR it didn't grow by at least 1,
            // treat it as a failure even though HTTP said 200.
            const arr = Array.isArray(data?.paymentProofs) ? data.paymentProofs : null;
            if (!arr || arr.length === 0) throw new Error('Server returned no proof entries — try again');
            return data;
          }
        } catch (err: any) {
          // AbortError / TypeError ("Failed to fetch") / network error path
          // — keep trying.
          lastError = err;
        }
        if (attempt < 3) await new Promise((r) => setTimeout(r, 800 * attempt));
      }
      throw lastError || new Error('Upload failed after 3 attempts');
    },
    onSuccess: (data: any) => {
      const count = Array.isArray(data?.paymentProofs) ? data.paymentProofs.length : 0;
      toast.success(`Payment proof saved · ${count} on record`);
      setFiles([]);
      setNote('');
      if (inputRef.current) inputRef.current.value = '';
      // Optimistically seed the cache with the fresh proofs from the
      // response so the panel re-renders even before the invalidate-driven
      // refetch round-trips. Belt-and-braces — both paths converge on the
      // same data.
      qc.setQueryData(['doc-onsite', onSiteId], (prev: any) =>
        prev ? { ...prev, paymentProofs: data.paymentProofs } : prev,
      );
      qc.invalidateQueries({ queryKey: ['doc-onsite', onSiteId] });
      qc.invalidateQueries({ queryKey: ['onsite-payers'] });
    },
    onError: (e: any) => {
      // Don't clear the form on error — operator can fix the issue and
      // retry without re-picking files.
      // eslint-disable-next-line no-console
      console.error('Payment proof upload failed', e);
      toast.error(e?.message || 'Upload failed — files still selected, click Upload to retry');
    },
  });

  const removeProof = useMutation({
    mutationFn: (proofId: string) => api.del(`/api/on-site-purchases/${onSiteId}/payment-proofs/${proofId}`),
    onSuccess: () => {
      toast.success('Proof removed');
      qc.invalidateQueries({ queryKey: ['doc-onsite', onSiteId] });
    },
    onError: (e: any) => toast.error(e?.message || 'Remove failed'),
  });

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  // Browser-close guard during an in-flight upload. Some operators have
  // reflexively closed the doc-modal or hit refresh while the multipart
  // POST was still in transit — the proof never landed and there was no
  // trace. Now we yell at them.
  useEffect(() => {
    if (!upload.isPending) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'A payment proof upload is in progress. Are you sure you want to leave?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [upload.isPending]);

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-3 space-y-3">
      <div className="text-xs font-semibold uppercase text-emerald-800 tracking-wide flex items-center gap-1">
        💸 Payment proofs
        <span className="text-[10px] text-slate-500 ml-1 font-normal normal-case">
          ({proofs.length} file{proofs.length === 1 ? '' : 's'})
        </span>
      </div>

      {proofs.length > 0 && (
        <ul className="space-y-1.5">
          {proofs.map((p) => {
            const isImg = /^image\//i.test(p.contentType || '') || /\.(jpe?g|png|gif|webp|bmp|heic|heif|avif)$/i.test(p.filename || '');
            const url = fileUrl(p.id);
            return (
              <li key={p._id} className="rounded border border-emerald-200 bg-white p-2 flex items-start gap-3">
                {isImg ? (
                  <button
                    type="button"
                    onClick={() => setLightbox({ url, filename: p.filename })}
                    className="rounded overflow-hidden border border-emerald-200 hover:border-emerald-400 shadow-sm cursor-zoom-in shrink-0"
                    title={`Preview ${p.filename}`}
                  >
                    <img src={url} alt={p.filename} className="block w-24 h-24 object-cover" loading="lazy" />
                  </button>
                ) : (
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 bg-white hover:bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-mono rounded px-2.5 py-1.5 shrink-0"
                    title={`Open/Download ${p.filename}`}
                  >
                    📄 <span className="truncate max-w-[160px]">{p.filename}</span>
                  </a>
                )}
                <div className="flex-1 min-w-0 text-xs">
                  <div className="flex items-baseline gap-2">
                    <span className="font-semibold text-slate-800 truncate">{p.filename}</span>
                    {p.paidBy && (
                      <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-medium">
                        Paid by {p.paidBy}
                      </span>
                    )}
                  </div>
                  {p.note && <div className="text-slate-700 mt-0.5 whitespace-pre-wrap">{p.note}</div>}
                  <div className="text-[10px] text-slate-500 mt-1 font-mono">
                    {p.uploadedByName || '—'} · {p.uploadedAt ? new Date(p.uploadedAt).toLocaleString('en-IN') : '—'}
                  </div>
                </div>
                <button
                  type="button"
                  className="text-slate-400 hover:text-red-600 text-xs"
                  onClick={() => {
                    if (confirm(`Remove proof "${p.filename}"?`)) removeProof.mutate(String(p._id));
                  }}
                  title="Remove this proof"
                  disabled={removeProof.isPending}
                >
                  ✕
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="border border-dashed border-emerald-300 rounded p-2 space-y-2 bg-white">
        <div className="text-[10px] uppercase text-slate-500 tracking-wide font-semibold">Add new proof</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase text-slate-500 block mb-0.5">Paid by</label>
            {customPaidBy ? (
              <input
                className="input text-xs"
                value={paidBy}
                onChange={(e) => setPaidBy(e.target.value)}
                placeholder="Type a new payer name…"
                autoFocus
              />
            ) : (
              <select
                className="input text-xs"
                value={paidBy}
                onChange={(e) => {
                  if (e.target.value === '__add__') {
                    setCustomPaidBy(true);
                    setPaidBy('');
                  } else {
                    setPaidBy(e.target.value);
                  }
                }}
              >
                <option value="">— select payer —</option>
                {payers.map((p) => <option key={p} value={p}>{p}</option>)}
                <option value="__add__">+ Add new payer…</option>
              </select>
            )}
            {customPaidBy && (
              <button
                type="button"
                className="text-[10px] text-slate-500 hover:text-slate-800 mt-0.5"
                onClick={() => { setCustomPaidBy(false); setPaidBy(''); }}
              >
                ← back to list
              </button>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 block mb-0.5">Note (optional)</label>
            <input
              className="input text-xs"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. UPI ref, cheque no…"
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              multiple
              className="text-xs"
              disabled={upload.isPending}
              onChange={(e) => {
                const list = e.target.files ? Array.from(e.target.files) : [];
                const tooLarge = list.find((f) => f.size > 20 * 1024 * 1024);
                if (tooLarge) {
                  toast.error(`"${tooLarge.name}" is over 20 MB`);
                  e.target.value = '';
                  return;
                }
                if (list.length > 5) {
                  toast.error('Max 5 files per upload');
                  e.target.value = '';
                  return;
                }
                setFiles(list);
              }}
            />
            <span className="text-[10px] text-slate-500">any type · max 5 · 20 MB each</span>
          </div>
          <button
            type="button"
            className="btn-primary text-xs disabled:opacity-50"
            disabled={files.length === 0 || upload.isPending}
            onClick={() => upload.mutate()}
          >
            {upload.isPending ? 'Uploading…' : 'Upload'}
          </button>
        </div>
        {files.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {files.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5 text-[10px] font-mono text-emerald-800">
                📎 {f.name} · {(f.size / 1024).toFixed(0)} KB
              </span>
            ))}
          </div>
        )}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-w-full max-h-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.url}
              alt={lightbox.filename}
              className="block max-w-[90vw] max-h-[85dvh] object-contain rounded shadow-2xl"
            />
            <button
              type="button"
              className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm"
              onClick={() => setLightbox(null)}
            >
              ✕
            </button>
            <div className="absolute bottom-2 left-2 right-2 text-white text-xs font-mono bg-black/50 px-2 py-1 rounded truncate" title={lightbox.filename}>
              {lightbox.filename}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GenericDoc({ title, endpoint, zIndex, onClose }: { title: string; endpoint: string; zIndex: number; onClose: () => void }) {
  const { data, isLoading, error } = useQuery<any>({
    queryKey: ['doc-generic', endpoint],
    queryFn: () => api.get(endpoint),
  });
  const docHeading = data?.indentNo || data?.billNo || data?.name || data?.code || data?.reqNo || title;
  return (
    <Modal title={`${title} — ${docHeading}`} onClose={onClose} maxWidth="max-w-3xl" zIndex={zIndex}>
      {isLoading && <Loading size="sm" />}
      {error && <div className="p-4 text-sm text-red-600">Could not load.</div>}
      {data && (
        <pre className="text-[11px] leading-snug bg-slate-50 border border-slate-200 rounded p-3 overflow-x-auto">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </Modal>
  );
}

// ---------- shared sub-components ----------

function ItemsTable({ items, mode, showBillRef }: { items: any[]; mode: 'issue' | 'grn' | 'po'; showBillRef?: boolean }) {
  if (!items.length) return <div className="text-sm text-slate-500 italic">No item details.</div>;
  const billCol = !!showBillRef && items.some((it) => typeof it.billRef === 'number' && it.billRef >= 0);
  // Transport applies to PO lines only (flat per-line freight). Show the
  // column + a goods/transport/grand-total breakup when any line carries it.
  const transportCol = mode === 'po' && items.some((it) => Number(it.transportCost) > 0);
  const goodsSum = items.reduce((s, it) => {
    const qty = Number(it.quantity ?? it.qty ?? it.receivedQty ?? 0);
    return s + Number(it.amount ?? qty * Number(it.rate ?? 0));
  }, 0);
  const transportSum = items.reduce((s, it) => s + (Number(it.transportCost) || 0), 0);
  const colCount = 6 + (mode !== 'po' ? 1 : 0) + (transportCol ? 1 : 0) + (billCol ? 1 : 0);
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Items</div>
      <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
        <thead className="text-xs uppercase text-slate-500 bg-slate-50">
          <tr>
            <th className="text-left px-2 py-1.5">#</th>
            <th className="text-left px-2 py-1.5">Spare</th>
            <th className="text-left px-2 py-1.5">Part Code</th>
            {mode !== 'po' && <th className="text-left px-2 py-1.5">Condition</th>}
            <th className="text-right px-2 py-1.5">Qty</th>
            <th className="text-left px-2 py-1.5">Unit</th>
            <th className="text-right px-2 py-1.5">Rate</th>
            {transportCol && <th className="text-right px-2 py-1.5" title="Flat freight/transport for this line">Transport</th>}
            <th className="text-right px-2 py-1.5">Amount</th>
            {billCol && <th className="text-left px-2 py-1.5">Bill</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((it, i) => {
            const code = displayPartCode(it);
            const name = it.itemName || (typeof it.partCode === 'object' ? it.partCode?.itemName : '') || '';
            const qty = Number(it.quantity ?? it.qty ?? it.receivedQty ?? 0);
            const rate = Number(it.rate ?? 0);
            const amount = Number(it.amount ?? qty * rate);
            return (
              <tr key={i}>
                <td className="px-2 py-1.5 text-slate-500">{i + 1}</td>
                <td className="px-2 py-1.5">{name}</td>
                <td className="px-2 py-1.5 font-mono text-xs">
                  {code ? <DocLink type="partcode" id={code} label={code} /> : <span className="text-amber-700" title="No part code on this line">—</span>}
                </td>
                {mode !== 'po' && <td className="px-2 py-1.5 text-xs uppercase">{it.condition || '—'}</td>}
                <td className="px-2 py-1.5 text-right font-mono">{qty}</td>
                <td className="px-2 py-1.5 text-xs">{it.qtyUnit || 'NOS'}</td>
                <td className="px-2 py-1.5 text-right font-mono">
                  {rate > 0 ? `₹${rate.toLocaleString('en-IN')}` : <span className="text-slate-400">—</span>}
                </td>
                {transportCol && (
                  <td className="px-2 py-1.5 text-right font-mono">
                    {Number(it.transportCost) > 0 ? `₹${Number(it.transportCost).toLocaleString('en-IN')}` : '—'}
                  </td>
                )}
                <td className="px-2 py-1.5 text-right font-mono">
                  {amount > 0 ? `₹${amount.toLocaleString('en-IN')}` : <span className="text-slate-400">—</span>}
                </td>
                {billCol && (
                  <td className="px-2 py-1.5 text-xs">
                    {typeof it.billRef === 'number' && it.billRef >= 0 ? `#${it.billRef + 1}` : '—'}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        {transportCol && (
          <tfoot className="bg-slate-50 text-xs">
            <tr><td colSpan={colCount - 1} className="px-2 py-1 text-right text-slate-500">Goods subtotal</td><td className="px-2 py-1 text-right font-mono">₹{goodsSum.toLocaleString('en-IN')}</td></tr>
            <tr><td colSpan={colCount - 1} className="px-2 py-1 text-right text-slate-500">Transport</td><td className="px-2 py-1 text-right font-mono">₹{transportSum.toLocaleString('en-IN')}</td></tr>
            <tr><td colSpan={colCount - 1} className="px-2 py-1 text-right font-semibold">Grand total</td><td className="px-2 py-1 text-right font-mono font-semibold">₹{(goodsSum + transportSum).toLocaleString('en-IN')}</td></tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function Remarks({ text }: { text: string }) {
  return (
    <div className="text-xs text-slate-600">
      <div className="text-[10px] uppercase text-slate-500 mb-1">Remarks</div>
      {text}
    </div>
  );
}

// Full approval + accounts trail as ONE chronological matrix: PO generated →
// each approval stage (Technical / Commercial / PO — with who + when + note) →
// VR minted → goods received (GRN) → bill submitted / processed / paid (with
// UTR + paid date when present). Answers "what status is it, at what time".
type TrailTone = 'slate' | 'emerald' | 'red' | 'amber' | 'sky';
const TRAIL_DOT: Record<TrailTone, string> = {
  slate: 'bg-slate-400', emerald: 'bg-emerald-500', red: 'bg-red-500', amber: 'bg-amber-500', sky: 'bg-sky-500',
};
const TRAIL_BADGE: Record<TrailTone, string> = {
  slate: 'bg-slate-100 text-slate-600', emerald: 'bg-emerald-100 text-emerald-700',
  red: 'bg-red-100 text-red-700', amber: 'bg-amber-100 text-amber-800', sky: 'bg-sky-100 text-sky-700',
};
function PoAccountsTrail({ data }: { data: any }) {
  type Row = { at?: string; stage: string; status: string; by?: string; detail?: ReactNode; ref?: { type: DocType; id: string; label: string }; tone: TrailTone };
  const rows: Row[] = [];

  rows.push({ at: data.createdAt, stage: 'PO generated', status: 'created', by: data.generatedBy?.name, tone: 'slate' });

  const chain: any[] = Array.isArray(data?.approvalChain) && data.approvalChain.length
    ? data.approvalChain
    : (Array.isArray(data?.approvals) ? data.approvals : []);
  chain.forEach((s: any) => {
    const st = String(s.status || 'Pending');
    const approvers = s.byName || s.byEmail || (Array.isArray(s.approverNames) ? s.approverNames.join(', ') : '') || (Array.isArray(s.approverEmails) ? s.approverEmails.join(', ') : '');
    rows.push({
      at: s.at || undefined,
      stage: s.ruleName || `Approval ${(s.stageIndex ?? 0) + 1}`,
      status: st,
      by: approvers,
      detail: s.note ? `“${s.note}”` : undefined,
      tone: /approv/i.test(st) ? 'emerald' : /reject/i.test(st) ? 'red' : 'amber',
    });
  });

  if (data.vrNo) rows.push({ stage: 'Voucher Reference minted', status: `VR ${data.vrNo}`, tone: 'sky' });

  const tl: any[] = Array.isArray(data.timeline) ? data.timeline : [];
  tl.forEach((ev: any) => {
    const type = String(ev.type || '');
    const p = ev.payload || {};
    const extra: string[] = [];
    if (p.status) extra.push(String(p.status));
    if (p.paidAt) extra.push(`paid ${fmtDateTime(p.paidAt)}`);
    if (p.utr) extra.push(`UTR ${p.utr}`);
    if (p.billAmount) extra.push(`₹${Number(p.billAmount).toLocaleString('en-IN')}`);
    rows.push({
      at: ev.at,
      stage: type === 'grn' ? 'Goods received (GRN)' : type === 'bill' ? 'Bill processed' : type === 'issue' ? 'Issued to site' : type,
      status: ev.summary || ev.ref || '',
      detail: extra.length ? extra.join(' · ') : undefined,
      ref: ev.ref ? { type: (type === 'grn' ? 'grn' : type === 'issue' ? 'issue' : 'bill') as DocType, id: String(p._id || ev.ref), label: ev.ref } : undefined,
      tone: type === 'grn' ? 'emerald' : type === 'bill' ? 'sky' : 'amber',
    });
  });

  // Chronological; undated (still-pending stages) sink to the bottom.
  rows.sort((a, b) => (a.at ? Date.parse(a.at) : Infinity) - (b.at ? Date.parse(b.at) : Infinity));
  if (!rows.length) return null;

  return (
    <div>
      <div className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Approval &amp; accounts trail</div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-3 py-1.5">Stage / event</th>
              <th className="text-left px-3 py-1.5">Status</th>
              <th className="text-left px-3 py-1.5">By</th>
              <th className="text-left px-3 py-1.5">When</th>
              <th className="text-left px-3 py-1.5">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r, i) => (
              <tr key={i} className="align-top">
                <td className="px-3 py-1.5 whitespace-nowrap">
                  <span className={`inline-block w-2 h-2 rounded-full mr-1.5 align-middle ${TRAIL_DOT[r.tone]}`} />
                  {r.stage}
                </td>
                <td className="px-3 py-1.5">
                  {r.ref
                    ? <DocLink type={r.ref.type} id={r.ref.id} label={r.ref.label} />
                    : <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${TRAIL_BADGE[r.tone]}`}>{r.status}</span>}
                  {r.ref && r.status && <span className="ml-1.5 text-slate-500 text-xs">{r.status}</span>}
                </td>
                <td className="px-3 py-1.5 text-slate-600 max-w-[160px] truncate" title={r.by || ''}>{r.by || '—'}</td>
                <td className="px-3 py-1.5 text-slate-500 font-mono text-xs whitespace-nowrap">{r.at ? fmtDateTime(r.at) : <span className="text-amber-600">pending</span>}</td>
                <td className="px-3 py-1.5 text-slate-600 break-words">{r.detail || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
